import type { InterestId, StrengthId, AmbitionId, LifePathId } from "../types";

export type ToneFlavor = "wry" | "eerie" | "tender" | "absurd" | "melancholy";

export interface InversionDef {
  id: string;
  ruleTitle: string;
  premise: string; // The core inverted assumption
  socialOrder: string;
  dailyPractice: string;
  civicVibe: string;
  localParadigm: string;
  forbiddenOrRevered: string;
  consequences: string[]; // >= 10 consequence templates
  dilemmas: string[]; // >= 6 open dilemmas
}

export const TONES: readonly ToneFlavor[] = ["wry", "eerie", "tender", "absurd", "melancholy"];

/**
 * 15 distinct Inverted Assumptions with >= 10 consequences each and >= 6 open dilemmas each.
 */
export const INVERSIONS: readonly InversionDef[] = [
  {
    id: "memory_currency",
    ruleTitle: "The Economy of Recollection",
    premise: "Personal memories are literal currency minted into copper-glass wafers, spent to buy shelter, and permanently erased upon transaction.",
    socialOrder: "Wealth is measured by how much of your childhood you still retain; the impoverished survive on clean slate amnesia.",
    dailyPractice: "Citizens carry wax sealers to shield sacred family afternoons from accidental expenditure during grocery bargaining.",
    civicVibe: "Quiet, contemplative arcades where merchants test memories for sensory crispness before accepting payment.",
    localParadigm: "Valuing authentic sensory recollection over fabricated digital ledgers or tangible commodities.",
    forbiddenOrRevered: "Hoarding childhood grief is revered as high-risk speculative collateral.",
    consequences: [
      "Contract disputes are resolved by dissolving the memory of the disagreement in a public kiln.",
      "Inheritance taxes require children to surrender ten vivid recollections of their deceased parents to the city vault.",
      "The wealthiest families maintain sprawling private estates purely by trading away memories of their worst mistakes.",
      "Public libraries operate as pawn shops where desperate students pawn early romances to purchase calculus textbooks.",
      "A black market thrives in synthetic nostalgia, selling cheap counterfeit memories of sunny seaside holidays.",
      "Criminals are sentenced to 'enrichment'—forced to memorize centuries of municipal sewer blueprints until their own names fade.",
      "Marriages are legally binding only when both partners exchange and immediately destroy their first impressions of each other.",
      "Architects construct buildings with tactile bronze friezes to trigger lost memories in bankrupt passersby.",
      "The elderly frequently wander into public fountains because they sold the memory of their front door key to afford winter soup.",
      "Artisans refuse paper money, accepting only the precise recollection of how lilac flowers smell after morning rain.",
      "Young lovers write extensive secret diaries to confirm that forgotten seasons actually took place.",
      "Debtors' prisons do not hold bodies; they hold minds until five years of cognitive recollection are systematically harvested."
    ],
    dilemmas: [
      "Whether to sell the last remaining memory of a mother's lullaby to fund an urgent medical cure for an ailing sister.",
      "Choosing between keeping the painful memory of a catastrophic workshop fire or pawning it to clear a bankrupt apprentice's debt.",
      "Holding an illicit cache of unminted memories that prove the city founder was an escaped convict, risking asset seizure if disclosed.",
      "Deciding whether to buy back a spouse's pawned memories of their wedding day, knowing the seller demands twenty years of professional craft knowledge in trade.",
      "Navigating the temptation to erase an agonizing betrayal by spending it, when that very betrayal forms the foundation of all current work.",
      "Choosing whether to certify a counterfeit memory for a starving widow who needs to purchase warm blankets before winter freeze."
    ],
  },
  {
    id: "unfinished_sentences",
    ruleTitle: "The Etiquette of Incompletion",
    premise: "It is considered grave social aggression to finish a sentence; meaning must always be completed by the listener in respectful silence.",
    socialOrder: "Orators who complete their thoughts are treated as authoritarian tyrants; wisdom lies entirely in delicate ellipses.",
    dailyPractice: "Conversations consist of rhythmic prompts where each speaker yields the air before reaching the predicate.",
    civicVibe: "A murmurous, highly observant city where public plazas echo with half-phrases and eloquent nods.",
    localParadigm: "Cultivating collective intuition and communicative restraint over dogmatic, exhaustive speech.",
    forbiddenOrRevered: "Punctuation marks denoting finality are banned from private correspondence.",
    consequences: [
      "Courts of law do not deliver verdicts; judges offer conditional beginnings and allow the assembly to exhale the outcome.",
      "Town criers announce emergencies by ringing an uncompleted bell cadence and letting the crowd deduce the danger.",
      "Literature consists entirely of opening clauses, requiring readers to assemble their own personal libraries of conclusions.",
      "Diplomacy between rival provinces proceeds at glacial speed because both ambassadors refuse to articulate the final treaty terms.",
      "Poets are paid by the syllable they deliberately withheld rather than the lines they printed.",
      "Children are disciplined not with reprimands, but with sudden, breathless pauses that invite remorseful reflection.",
      "Actors in municipal theaters rehearse only the first half of classic plays, relying on the audience to finish the fifth act.",
      "Street vendors pitch goods with inviting adverbs and watch customers finish the bargaining equation with silent hand gestures.",
      "Scientific papers state hypotheses with crystalline rigor and leave the conclusions section deliberately blank for peers.",
      "Philosophers argue for decades over whether a completely unspoken thought constitutes the highest form of flattery.",
      "Funerals are celebrated in absolute silence, as completing any sentence about a departed life is seen as terminating their soul.",
      "Contracts are printed with trailing commas, legally obligating both signatories to maintain open-ended trust."
    ],
    dilemmas: [
      "Whether to break etiquette by shouting a complete sentence to warn a distracted pedestrian of an oncoming cargo cart.",
      "Choosing whether to articulate the final proof of a revolutionary physical theorem or leave it unsaid to preserve intellectual modesty.",
      "Navigating an accusation of civic arrogance leveled by a municipal rival because a greeting accidentally concluded with a period.",
      "Deciding whether to voice an explicit confession of love when the beloved interprets ongoing silence as emotional indifference.",
      "Balancing the demand to complete a critical engineering safety checklist against deep communal revulsion toward definitive statements.",
      "Choosing whether to expose a fraudulent guild charter whose loopholes exist entirely within the unstated clauses of its text."
    ],
  },
  {
    id: "dead_proxy_voting",
    ruleTitle: "The Ancillary Franchise",
    premise: "The deceased do not lose civic rights; their voting franchise passes to their designated living heir, who must cast it strictly according to ancestral tastes.",
    socialOrder: "Living politicians campaign in cemeteries; families with seven generations of intact proxies hold unbreakable parliamentary blocs.",
    dailyPractice: "Citizens maintain ledger rooms filled with ancestral portraits, consulting astrological charts and old diaries before casting ballots.",
    civicVibe: "A solemn, neo-gothic metropolis where polling booths feature dual curtains for the living voter and the ancestral shadow.",
    localParadigm: "Binding contemporary governance to the deep continuous consent of departed generations.",
    forbiddenOrRevered: "Changing an ancestor's preferred political party is punished as spiritual treason.",
    consequences: [
      "Elections require ballot boxes large enough to hold heavy stone tablets engraved with ancestral seals.",
      "Real estate zoning laws from two centuries ago remain active because the original landowners consistently vote against paved roads.",
      "Candidates debate historical ghosts, spending campaign budgets on floral wreaths and classical chamber concerts in mausoleums.",
      "Genealogical detectives command enormous retainers to verify whether a third great-uncle would have supported sewage levies.",
      "Youth movements struggle to pass technological subsidies against the overwhelming conservative vote of eighteenth-century millers.",
      "Divorce proceedings involve dividing ancestral proxies, turning family splits into complex parliamentary redistricting battles.",
      "New technologies must be framed as restorations of antique methods to survive ancestral plebiscites.",
      "Hospitals maintain voting registrars beside deathbeds so the newly deceased can register their proxy before their pulse cools.",
      "Taxation bills are rejected routinely because centuries of departed taxpayers refuse to fund modern electrical lighting.",
      "Public statues are erected not to commemorate leaders, but to bribe the aesthetic preferences of influential dead dynasties.",
      "A black market operates in forged Victorian deathbed codicils granting voting proxies to radical political factions.",
      "University faculties remain dominated by emeritus professors whose votes continue to be cast by their academic disciples."
    ],
    dilemmas: [
      "Whether to cast an ancestor's decisive proxy vote against a modern clean water project that the ancestor would have distrusted as witchcraft.",
      "Choosing between honoring a grandfather's sworn political pledge or voting for a progressive reform that saves a contemporary neighborhood.",
      "Deciding whether to reveal that a prestigious ancestral proxy held by the family was forged during a nineteenth-century cholera panic.",
      "Navigating the pressure from living siblings who want to sell off ancestral voting rights to clear immediate family debts.",
      "Choosing whether to disown an inheritance of thirty reactionary ancestral proxies to regain personal political autonomy.",
      "Balancing filial reverence with the urgent need to approve emergency flood dyke funding opposed by stubborn ghost electors."
    ],
  },
  {
    id: "outlawed_aptitude",
    ruleTitle: "The Prohibition of Natural Gifts",
    premise: "Whatever trade or art comes most naturally to a citizen is strictly banned by state ordinance; everyone must make their living at what they do worst.",
    socialOrder: "Brilliant natural mathematicians are assigned to manage chaotic vegetable stalls, while clumsy artisans are appointed master surgeons.",
    dailyPractice: "Workers openly boast about how difficult, counterintuitive, and agonizingly clumsy their daily tasks feel.",
    civicVibe: "A tender, fiercely cooperative society where everyone constantly forgives everyone else's inevitable blunders.",
    localParadigm: "Eradicating meritocratic arrogance by grounding civic life in shared, earnest struggle.",
    forbiddenOrRevered: "Effortless grace is hidden like contraband; public labor must visibly display sweating exertion.",
    consequences: [
      "Natural poets are conscripted into municipal sewage auditing, writing balance sheets with excruciating slowness.",
      "Musical prodigies are restricted to silence and appointed chief grain measurers, filling sacks with clumsy concentration.",
      "A thriving underground cellar scene allows banished painters to secretly mix oils and paint masterpieces in complete darkness.",
      "Apprenticeship exams test for lack of talent; the applicant who fails the trial most thoroughly is immediately hired.",
      "Public buildings lean at charming, precarious angles because the chief stonemasons all possessed natural talents for pastry baking.",
      "Performance reviews reward documented incompetence, provided the employee demonstrates maximum sweating sincerity.",
      "Pride is considered an antisocial illness; citizens seek counseling whenever a task feels effortlessly enjoyable.",
      "The city's greatest mechanical innovations arise from clumsy people inventing elaborate automated jigs to avoid manual mistakes.",
      "State inspectors conduct surprise fluency audits, arresting anyone who looks too relaxed while playing the violin.",
      "Neighborly mutual-aid thrives because everyone requires urgent assistance to complete the simplest household repairs.",
      "Competence is treated as an embarrassing private secret shared only with closest lovers behind locked shutters.",
      "Diplomats are selected from the most hot-headed, tactless citizens, resulting in shockingly blunt but entirely transparent treaties."
    ],
    dilemmas: [
      "Whether to secretly use natural mathematical brilliance to fix an unstable bridge calculation, risking exile if discovered.",
      "Choosing between accepting a promotion in a field of catastrophic ineptitude or confessing a hidden aptitude for clockwork mechanics.",
      "Deciding whether to report an underground salon where gifted musicians play exquisite melodies without permission.",
      "Navigating the shame of realizing you have accidentally grown competent at your assigned profession of heavy stone carving.",
      "Choosing whether to allow a clumsy peer to perform a delicate optical surgery on an elder or intervene and expose forbidden skill.",
      "Balancing the moral duty to shield an apprentice whose natural talent is beginning to show in every stroke of the brush."
    ],
  },
  {
    id: "nocturnal_commonwealth",
    ruleTitle: "The Inverted Solstice",
    premise: "Daylight is designated as the sacred sleep period for fear of solar madness; all civic life, commerce, and governance occur strictly between dusk and dawn.",
    socialOrder: "Sunlight is treated as hazardous, radioactive spiritual contamination; waking after sunrise is a sign of moral degeneracy.",
    dailyPractice: "Windows are sealed with heavy slate panels; children play under sodium vapor lamps and learn astronomy before botany.",
    civicVibe: "A glowing, neon-lit nocturnal paradise of night markets, lantern-lit canals, and moonlit academic academies.",
    localParadigm: "Centering communal existence around the calm, cooling mystery of the stars rather than blinding solar tyranny.",
    forbiddenOrRevered: "Opening a shutter during high noon is punished with mandatory sensory deprivation.",
    consequences: [
      "Rush hour peaks at 2:00 AM, with bicycle couriers navigating glowing cobblestone lanes beneath moonlit cloud banks.",
      "Solar eclipses are celebrated as terrifying emergencies where daytime guards patrol streets with blindfolds.",
      "The city's economy runs on bioluminescent algae lanterns and hand-cranked dynamo streetlights.",
      "Botanists cultivate nocturnal nightshades, ghost orchids, and luminous mosses, treating green chlorophyll as an unnatural anomaly.",
      "Sunbathers are viewed as dangerous cultists who worship the burning eye of sky madness in remote mountain caves.",
      "Morning tea is consumed at 7:00 PM; evening banquets conclude with toasts to the setting moon at 5:00 AM.",
      "Architecture features zero skylights, relying on deep basalt tunnels and mirrored periscopes tuned exclusively to starlight.",
      "Outdoor athletics take place under heavy velvet canopies illuminated by floating phosphor balloons.",
      "The greatest civic insult is to tell someone their ideas are 'as glaring and unsubtle as noon.'",
      "Criminals are sentenced to 'solar exposure'—forced to sit on a hillside for fifteen minutes wearing dark goggles.",
      "Clocks run backward from midnight, counting down the precious hours of dark before the dreaded daylight curfew.",
      "Poets celebrate the deep velvet hues of midnight fog as the supreme manifestation of divine love."
    ],
    dilemmas: [
      "Whether to rescue a stray child trapped outside the city gates as the lethal morning sun begins to crest the horizon.",
      "Choosing whether to reveal an illicit botanical study that proves certain crops yield four times more grain under direct sunlight.",
      "Deciding whether to join an underground circle of dawn-watchers who gather in secret attics to glimpse the forbidden sunrise.",
      "Navigating the social stigma of suffering from a circadian disorder that makes you wake up wide alert at 11:00 AM.",
      "Choosing whether to report a municipal lamp-lighter who is systematically dimming public lanterns to hoard fuel for private lanterns.",
      "Balancing loyalty to the Nocturnal Conclave against an urgent scientific expedition requiring measurement of solar flares."
    ],
  },
  {
    id: "fame_for_omission",
    ruleTitle: "The Canon of Restraint",
    premise: "Reputation, titles, and public monuments are awarded solely for things a person deliberately chose NOT to do; action is suspect, restraint is divine.",
    socialOrder: "Heroes are celebrated for wars they refused to start, buildings they declined to erect, and books they left unwritten.",
    dailyPractice: "Curriculums teach the art of graceful refusal; resumes list seventy-five lucrative temptations successfully declined.",
    civicVibe: "A serene, minimalist commonwealth where open spaces are cherished because no one permitted construction there.",
    localParadigm: "Elevating ethical abstention and ecological non-interference above aggressive productivity.",
    forbiddenOrRevered: "Publicly claiming credit for a completed physical project is regarded as vulgar exhibitionism.",
    consequences: [
      "The central city square features empty marble plinths dedicated to generals who resigned their commissions at the brink of invasion.",
      "Authors achieve literary immortality by announcing the plot of a masterpiece and then publicly burning their notes.",
      "Architects receive gold medals for convincing city councils to leave marshlands completely undeveloped.",
      "Citizens receive tax rebates for every professional contract they turned down in favor of quiet contemplation.",
      "Criminal courts reward defendants who can prove they planned an elaborate heist but abandoned it out of moral fatigue.",
      "Children are praised at dinner for leaving the last plum untouched on the wooden bowl.",
      "Industrialists are judged not by factory output, but by the number of patents they mothballed to prevent ecological ruin.",
      "Monuments carry plaques that read: 'Here stands nothing, thanks to the heroic restraint of Councilor Thorne.'",
      "Inventors who discover world-altering formulas are granted hereditary pensions on the condition they never build a prototype.",
      "Political elections are won by the candidate who promises to pass the fewest laws and abolish three administrative bureaus.",
      "A person's obituary consists of a detailed list of honors, inheritances, and romantic affairs they gracefully refused.",
      "Apprentices spend three years learning how to sit at a workbench without touching the tools until true necessity demands."
    ],
    dilemmas: [
      "Whether to build a desperately needed medical clinic, knowing you will lose your civic standing as a champion of architectural restraint.",
      "Choosing between publishing a life-saving treatise on crop blight or burning the manuscript to claim the supreme laurel of abstention.",
      "Deciding whether to intervene in a regional dispute when your entire reputation is built on maintaining absolute diplomatic neutrality.",
      "Navigating the guilt of secretly building a beautiful wooden sailboat in an abandoned cellar when manual construction is frowned upon.",
      "Choosing whether to accept an honorary pension for refusing a bribe, when in truth you simply forgot to attend the secret meeting.",
      "Balancing the personal urge to create tangible art against a culture that demands creative ideas remain pure, unmanifested potentials."
    ],
  },
  {
    id: "reversed_seniority",
    ruleTitle: "The Dominion of Toddlers",
    premise: "Constitutional authority decreases with age; infants hold supreme legislative veto, teenagers run the courts, and the elderly are treated as reckless wards.",
    socialOrder: "Grandparents are forbidden from driving or signing contracts without a written permission slip from an eleven-year-old guardian.",
    dailyPractice: "Town halls feature nursery cribs where baby cries decide city budgets; nursery rhymes are studied as statutory law.",
    civicVibe: "A kinetic, wildly imaginative metropolis painted in primary colors, balanced by weary elder advisors working in the shadows.",
    localParadigm: "Prioritizing uncorrupted moral clarity and playful wonder over entrenched institutional cynicism.",
    forbiddenOrRevered: "Displaying weary adult pragmatism in council chambers is fined as treasonous defeatism.",
    consequences: [
      "Municipal zoning laws mandate giant sandbox plazas and treehouse observation platforms in all commercial districts.",
      "Supreme Court judges are selected from eighth-grade debate teams because their sense of fairness has not yet rotted into compromise.",
      "Retirement homes are surrounded by gentle velvet barriers to keep reckless sixty-year-olds from engaging in unregulated philosophy.",
      "Diplomatic treaties are signed with colorful finger paints, binding empires to promises made over chocolate biscuits.",
      "Taxation is levied in shiny glass marbles, rare stickers, and promises to read three bedtime stories before dusk.",
      "University professors must submit their dissertations to a panel of seven-year-olds to prove their ideas are not boring.",
      "Police forces carry water pistols and whistle rings, resolving riots by declaring mandatory afternoon nap times.",
      "Adults over forty are legally classified as 'delicate dreamers' who must be sheltered from the grim realities of high governance.",
      "City budgets allocate forty percent of public expenditure to carousel maintenance and public confectionery fountains.",
      "Military forces are disbanded because twelve-year-old generals agree that getting muddy on a soccer field is vastly superior to war.",
      "Elderly philosophers write underground pamphlets demanding voting rights for citizens who have lived long enough to know better.",
      "Promotions in civil service require passing rigorous obstacle courses involving rope swings and hopscotch grids."
    ],
    dilemmas: [
      "Whether to overturn a ten-year-old mayor's decree to pave an ancient forest into a giant roller-skate rink.",
      "Choosing between obeying a toddler magistrate's whimsical trade embargo or secretly smuggling grain to prevent famine.",
      "Deciding whether to help an aging mentor forge a youth certificate so they can continue conducting astronomical research.",
      "Navigating the chaos when a twelve-year-old chief justice orders the immediate abolition of all bedtimes across three counties.",
      "Choosing whether to confess to a junior inspector that you secretly read dry economic treatises after nine o'clock at night.",
      "Balancing the genuine moral innocence of child rulers against their total blindness to long-term geological infrastructure decay."
    ],
  },
  {
    id: "mandatory_pseudonyms",
    ruleTitle: "The Mask of Anonymity",
    premise: "Birth names are destroyed at age seven; every citizen must adopt a brand new persona, trade, and registered name every four years by lottery.",
    socialOrder: "No one can build a permanent empire; the master architect of today becomes the lighthouse keeper of tomorrow.",
    dailyPractice: "Citizens celebrate 'Unmasking Eve' by packing suitcases, surrendering their keys, and receiving their next identity packet.",
    civicVibe: "A fluid, intensely present society where people treat every stranger with deep curiosity, knowing they might have been a king yesterday.",
    localParadigm: "Preventing class calcification and dynastic tyranny through radical, periodic reinvention.",
    forbiddenOrRevered: "Attempting to contact your former family under an expired name is a severe federal misdemeanor.",
    consequences: [
      "Family homes are leased for exactly forty-eight months before rotating to the next lottery cohort.",
      "Personal libraries are left behind in each house, creating generational communal archives of disparate reading tastes.",
      "Monuments commemorate deeds rather than names, listing 'A Weaver in the Third Ward' instead of individual heroes.",
      "Lovers develop complex secret handshakes and coded poetic ciphers to rediscover each other across quadrennial resets.",
      "Debts do not follow individuals; credit ratings reset to baseline every four years, preventing hereditary poverty.",
      "Guild mastery is collective: trade manuals are written with absolute clarity so any novice can pick up the tools immediately.",
      "Biographies are strictly illegal; literature focuses entirely on ensemble casts and situational parables.",
      "Passports carry finger-prints and iris patterns, but the name field is a wipeable slate rewritten at every border post.",
      "Citizens carry brass token rings engraved with previous professions, worn as private bracelets under coat sleeves.",
      "Funerals honor whatever name the deceased held that month, celebrated by whoever happens to be their current neighbor.",
      "The state archives hold millions of sealed envelopes connecting past names to present identities, guarded by blind archivists.",
      "Social status is impossible to maintain, so people invest heavily in cultivating charm, empathy, and conversational grace."
    ],
    dilemmas: [
      "Whether to deliberately fail a quadrennial trade lottery to stay near an apprentice who still needs your guidance.",
      "Choosing between surrendering a life's work of botanical research to an anonymous successor or hiding your notebooks in a hollow tree.",
      "Deciding whether to reveal your former identity to a new lover who happens to be the daughter of your former bitterest rival.",
      "Navigating the agony of watching your biological child walk past on the street under a new name, unable to reach out without penalty.",
      "Choosing whether to accept the supreme title of City Director when your four-year rotation expires in three weeks.",
      "Balancing the liberation of starting fresh against the accumulating sorrow of leaving behind four distinct lives."
    ],
  },
  {
    id: "silence_as_property",
    ruleTitle: "The Real Estate of Decibels",
    premise: "Acoustic silence is a finite, tradeable natural resource; noise is free, but a quiet room costs more per hour than fine vintage wine.",
    socialOrder: "The ultra-wealthy dwell in sound-dampened velvet towers; the poor live in a deafening, 24-hour cacophony of brass and iron.",
    dailyPractice: "People speak in shouting rhymes in public streets, then pay coin by the minute for a padded brass booth to whisper private grief.",
    civicVibe: "A booming, percussive urban landscape where quietness is the ultimate luxury and silence merchants carry heavy lead baffling.",
    localParadigm: "Treating acoustic tranquility as a precious ecological commons that human activity constantly depletes.",
    forbiddenOrRevered: "Unlicensed whispering in public parks is penalized as acoustic theft.",
    consequences: [
      "Belltowers chime every fifteen minutes, ensuring that non-paying citizens remain saturated with civic clatter.",
      "Sound-absorbing moss is harvested from high mountain crevices and sold by the gram in luxury upholstery shops.",
      "Courts of law require defendants to purchase three minutes of silence before making their closing arguments.",
      "Hospitals charge premium rates for recovery wards where cardiac monitors are muted with down feathers.",
      "A black market operates in illegal cork baffles and earplugs smuggled from overseas rubber plantations.",
      "Musicians are treated as environmental polluters unless they purchase municipal acoustic licenses for each trombone.",
      "Libraries are fortresses with six-foot thick felt gates, charging a gold sovereign per hour for access to quiet study carrels.",
      "Marriage proposals take place in rented 'Stillness Lounges' where lovers can hear each other's heartbeats for the first time.",
      "Factory workers develop complex manual sign languages to communicate across the roar without going hoarse.",
      "The city's elite wear shoes made of owl feathers, stepping through marble colonnades without making a single click.",
      "Public executions consist of placing the condemned in an utterly silent chamber until the lack of noise drives them to confession.",
      "Slum neighborhoods celebrate annual 'Riot of Noise' festivals where everyone bangs iron kettles to drown out the landlords' quiet."
    ],
    dilemmas: [
      "Whether to spend your entire life savings on ten minutes of absolute silence to comfort a dying parent in their final breath.",
      "Choosing between installing illegal acoustic baffling in a children's bedroom or paying the extortionate municipal decibel tax.",
      "Deciding whether to sabotage a private acoustic conduit that siphons tranquility from a working-class neighborhood to a luxury villa.",
      "Navigating the moral hazard of working as an acoustic debt collector who strips sound-absorbing felt from defaulted tenants.",
      "Choosing whether to publish an open-source blueprint for a cheap sound-dampening clay brick, risking lawsuits from the Silence Guild.",
      "Balancing personal addiction to the intoxicating peace of acoustic sanctuaries against solidarity with your noisy community."
    ],
  },
  {
    id: "obligation_to_err",
    ruleTitle: "The Doctrine of the Flaw",
    premise: "Perfection is viewed as an intolerable cosmological crime that invites cosmic wrath; every manufactured good, speech, and treaty must contain an intentional defect.",
    socialOrder: "Quality control inspectors do not search for errors; they search for flaws, penalizing any work that looks too neat or precise.",
    dailyPractice: "Tailors snip one button off new coats; bakers drop a single grain of sea salt into sweet custards; architects angle one pillar two degrees off true.",
    civicVibe: "A warm, delightfully crooked city where every facade, carriage, and clock displays a deliberate, loving imperfection.",
    localParadigm: "Inscribing human fallibility and modesty into every act of creation to ward off hubris.",
    forbiddenOrRevered: "Symmetrical perfection is regarded as demonic arrogance.",
    consequences: [
      "Watchmakers construct chronometers that gain exactly four minutes every Thursday to honor temporal elasticity.",
      "Printers introduce three typographical errors per page, treating completely error-free books as blasphemous curses.",
      "Surgeons leaving an incision will deliberately tie one suture with mismatched thread to keep the patient humble.",
      "Lawyers insert an absurd, unenforceable clause about dancing geese into every commercial lease.",
      "Bridge builders celebrate the 'Sacrificial Wobble'—a suspension cable deliberately calibrated to hum in minor keys during wind.",
      "Painters smudge the corner of their portraits with charcoal before exhibiting them in public salons.",
      "Public statues feature slightly oversized ears or mismatched noses, honoring the flawed reality of their subjects.",
      "Marriage vows include a mandatory promise to commit at least one comical household blunder every Sunday.",
      "Astronomers who calculate planetary orbits to ten decimal places round down to three to avoid insulting the stars.",
      "Children are taught calligraphy by first learning where to place the ceremonial ink blot.",
      "The supreme civic award is the 'Golden Chipped Chalice', bestowed upon artisans who turned a disastrous blunder into art.",
      "A black market caters to obsessive perfectionists who secretly buy illegal, razor-sharp symmetrical scissors."
    ],
    dilemmas: [
      "Whether to build a flood barrier with the legally mandated intentional crack, knowing a once-in-a-century storm is approaching.",
      "Choosing between submitting a flawless mathematical proof to save an academic project or introducing a deliberate error to satisfy the guild.",
      "Deciding whether to repair an heirloom clock that an ancestor built with an intentional fault that now causes it to jam every winter.",
      "Navigating the rage of an influential client who rejects a masterwork because you forgot to make it sufficiently imperfect.",
      "Choosing whether to conceal an apprentice's accidental masterpiece of pure symmetry or vandalize it with a chisel as the law demands.",
      "Balancing deep pride of craftsmanship with the genuine spiritual comfort that deliberate flaws bring to an anxious community."
    ],
  },
  {
    id: "borrowed_shadows",
    ruleTitle: "The Exchange of Silhouettes",
    premise: "Shadows are separable garments of dense optical resonance; they are traded, loaned to friends for protection against sunburn, or pawned during hardship.",
    socialOrder: "A person with no shadow is either destitute or a reckless outlaw; wearing an ornate, triple-layered silhouette signals nobility.",
    dailyPractice: "Citizens untie their shadows before entering bathhouses and hang them on numbered brass pegs beside their umbrellas.",
    civicVibe: "A visually mesmerizing, noir-dappled city where silhouettes dance independently along lamp-lit alleys.",
    localParadigm: "Viewing the personal shadow as an externalized second soul that can be shared with loved ones.",
    forbiddenOrRevered: "Stealing another person's silhouette on a moonlit night is punished by casting the thief into absolute darkness.",
    consequences: [
      "Lovers exchange shadows on their wedding day, walking hand in hand with each other's contours trailing behind them.",
      "Shadow tailors alter silhouettes with silver scissors, widening the shoulders of timid men or adding floral contours to dresses.",
      "Mourners wear the long, heavy shadows of their deceased kin for a year of ritual remembrance.",
      "Night watchmen carry lanterns that project giant lion shadows to intimidate rowdy tavern patrons.",
      "Children play shadow tag with real stakes, racing to peel off each other's silhouettes across sunlit cobblestones.",
      "Pawn brokers keep long wooden racks filled with rolled-up silhouettes, charging interest based on how crisp the edges remain.",
      "Actors rent dramatic, heroic silhouettes to project majestic stage presence during classical Shakespearean tragedies.",
      "An unpinned shadow will occasionally wander into an orchard at noon to pick windfallen apples on its own accord.",
      "Criminals clip their shadows to fence posts as optical alibis while committing nighttime burglaries three blocks away.",
      "Hospitals wrap shivering fever patients in dense, warm double-shadows borrowed from healthy athletes.",
      "A person whose shadow has grown faded or threadbare is prescribed two weeks of seaside sunbathing and honey cakes.",
      "Public lanterns are equipped with shadow-filters that cast intricate kaleidoscopic silhouettes across evening markets."
    ],
    dilemmas: [
      "Whether to pawn your beloved childhood shadow to pay for an emergency prescription of medicine for a younger sibling.",
      "Choosing between returning a loaned shadow that grants you extraordinary stage charisma or keeping it to secure your career.",
      "Deciding whether to reveal that you purchased an illicit black-market shadow that once belonged to a notorious political exile.",
      "Navigating the confusion when your own shadow refuses to re-attach after an evening spent in a philosophical salon.",
      "Choosing whether to lend your shadow to a companion who needs to disguise themselves while fleeing an unjust court summons.",
      "Balancing the personal intimacy of your biological silhouette against the social requirement to wear a formal corporate shadow to work."
    ],
  },
  {
    id: "liquid_time",
    ruleTitle: "The Fluidity of Chronology",
    premise: "Time is not a uniform river; it pools in valleys and evaporates on mountaintops, so twenty minutes downtown equals four hours on the coast.",
    socialOrder: "Commuters plan journeys based on barometric chronometry; mountain hermits live for subjective centuries while dockworkers age in fast forward.",
    dailyPractice: "Pocket watches have three mercury dials measuring pressure, altitude, and current temporal viscosity.",
    civicVibe: "A surreal, terraced metropolis where people yell across ravines to friends moving at twice their physical speed.",
    localParadigm: "Navigating life as a variable temporal terrain rather than submitting to the artificial tyranny of rigid seconds.",
    forbiddenOrRevered: "Synchronizing two clocks between different altitude zones is considered an act of heresy.",
    consequences: [
      "Courts hold trials in low-lying river valleys so tedious administrative proceedings conclude in twenty subjective minutes.",
      "Lovers build cabins on mountain ridges to stretch a summer romance across eighty subjective calendar years.",
      "Bakers ferment sourdough on high terraces where yeast has four days of time while the oven heats downstairs in fifteen minutes.",
      "Railroad timetables are spiral-bound scrolls of fluid calculus adjusted daily according to morning fog levels.",
      "Athletes train in heavy temporal eddies at the bottom of quarries, developing lightning-fast reflexes before returning to the plains.",
      "Old age pensions are calculated not by birthdays, but by the displacement of liquid mercury inside a citizen's personal hourglass.",
      "Funerals are held on the highest windy crags so mourners can spend a whole quiet lifetime saying goodbye in an afternoon.",
      "Bank vaults are submerged in stagnant swamp waters where time flows like molasses, stalling compound interest.",
      "Long-distance telephone calls sound like whale song or insect chattering unless converted by complex hydraulic slowing valves.",
      "Citizens carry thermos flasks filled with mountain air to inhale when they need an extra ten minutes before a difficult deadline.",
      "Children run down hillsides to grow up faster, while grandparents walk slowly uphill to savor the remaining light.",
      "A thriving trade exists in 'temporal ice'—blocks of frozen high-altitude air used to preserve fresh strawberries for years."
    ],
    dilemmas: [
      "Whether to move to the high mountain terraces to live a three-hundred-year life, knowing you will lose touch with valley friends who will die in a decade.",
      "Choosing between taking an express job in the deep river basin to make fast cash or preserving your youthful body on the ridges.",
      "Deciding whether to sell a canister of high-altitude temporal gas to an unscrupulous executive who wants to jump past a prison sentence.",
      "Navigating a romantic relationship where your partner works at a low-altitude shipyard and ages three times faster than you.",
      "Choosing whether to break temporal quarantine to deliver emergency penicillin to an isolated village trapped in a frozen time pool.",
      "Balancing the sweet peace of sluggish mountain time with the dynamic urgency of the fast-moving valley workshops."
    ],
  },
  {
    id: "edible_architecture",
    ruleTitle: "The Pastry Commonwealth",
    premise: "Building materials must be entirely digestible and compostable; all homes, bridges, and walls are baked from hardened, weather-resistant doughs and sugars.",
    socialOrder: "Architects are certified master pastry chefs; urban renewal happens through neighborhood feasts that eat old neighborhoods down to bedrock.",
    dailyPractice: "Citizens prune edible caramel eaves before rainstorms and bake repairs to front doors with portable stone ovens.",
    civicVibe: "A fragrant, gingerbread-scented metropolis of golden biscuit arches, spiced sugar windowpanes, and sourdough foundations.",
    localParadigm: "Eradicating permanent industrial waste by making the entire built environment part of the organic food cycle.",
    forbiddenOrRevered: "Building with permanent stone or iron is banned as a violent refusal to nourish the future.",
    consequences: [
      "Winter blizzards bring severe housing emergencies as ravenous wildlife attempts to devour public library colonnades.",
      "When a family outgrows their house, they invite three hundred friends over to eat the second floor down to the joists.",
      "City councils declare mandatory bread-baking days following heavy rainstorms to repair eroded bridge piers.",
      "Bank vaults are constructed from sixty layers of dense, tooth-shattering rock-sugar to prevent burglary.",
      "Bridges are reinforced with toasted rye sourdough beams that grow harder and more structural with age and sun exposure.",
      "Windows are cast from translucent spun barley sugar, filling parlors with warm amber light and a faint scent of vanilla.",
      "Slum landlords are sued if their tenant housing begins to spoil, turn moldy, or taste insufficiently nutritious.",
      "Street sweepers collect crumbs and crushed wafer pavers to feed municipal flocks of passenger pigeons.",
      "A major architectural award is granted based on both structural wind-resistance and exquisite mouthfeel of the entryway corbels.",
      "Military defenses consist of burning chili-infused biscuit walls that emit tear-inducing spicy smoke when attacked.",
      "Citizens carry butter knives in their lapels to shave an afternoon snack off municipal garden balustrades.",
      "Poverty is impossible because anyone who is hungry can legally walk up to any municipal building and eat a biscuit cornice."
    ],
    dilemmas: [
      "Whether to eat through the eastern wall of your ancestral bakery-house during a severe famine or risk starving to preserve heritage.",
      "Choosing between building a bridge out of affordable soft sourdough that will need replacing in three years or expensive stone-sugar.",
      "Deciding whether to report a group of hungry orphans who have gnawed a structural hole through the base of the municipal clocktower.",
      "Navigating the civic scandal when an architect uses cheap synthetic shortening that makes an entire residential block smell like rancid lard.",
      "Choosing whether to demolish an obsolete historical monument by hosting a massive feast or leaving it to slowly melt in autumn rains.",
      "Balancing the artistic urge to sculpt intricate spun-sugar spires against the reality of aggressive local crow populations."
    ],
  },
  {
    id: "names_as_currency",
    ruleTitle: "The Titular Economy",
    premise: "You do not own your name; names are leased from the Municipal Registry for finite periods, and running out of coins leaves you nameless and legally invisible.",
    socialOrder: "The ultra-wealthy hold three-syllable hereditary names of majestic lineage; the working poor lease brief, single-syllable monosyllables by the week.",
    dailyPractice: "Citizens check their name balance at copper meters before boarding ferries or entering government offices.",
    civicVibe: "A crisp, transactional society where introducing yourself carries literal financial weight and syllables are weighed like gemstones.",
    localParadigm: "Treating identity as a civic utility that must be actively earned and maintained through societal contribution.",
    forbiddenOrRevered: "Uttering an expired name in public spaces is fined as linguistic counterfeiting.",
    consequences: [
      "Names with noble vowel harmonies command exorbitant weekly auction prices at the central bourse.",
      "When a citizen's account drops to zero, they wear a brass blank collar and are addressed only as 'Citizen Null' until their next paycheck.",
      "Parents save for a decade to purchase a three-year lease on a prestigious name for their child's high school graduation.",
      "Poets barter evocative, melodious pseudonyms in back-alley taverns, trading a soft 'Aveline' for three bags of winter coal.",
      "Courts sentence white-collar embezzlers to five years under hideous, cacophonous names like 'Grunk' or 'Pflueger'.",
      "Marriage contracts determine whether partners will pool their capital to afford a hyphenated, two-name luxury suite.",
      "Job applicants with longer, multi-syllabic names are automatically assumed to possess substantial creditworthiness.",
      "Street urchins pool pennies to share a single short name between five companions on a rotating daily schedule.",
      "Billionaires hoard historical names, keeping names like 'Alexander' or 'Beatrice' locked in secure corporate trusts.",
      "A black market sells illegal 'shadow syllables'—unregistered guttural sounds inserted between first and last names.",
      "Charities organize 'Name Drives' where benefactors donate spare family names to homeless veterans.",
      "The city's greatest monuments are inscribed with barcode numbers because the names of the architects expired centuries ago."
    ],
    dilemmas: [
      "Whether to spend your last month's rent money to retain your late grandmother's beloved name or surrender it to public auction.",
      "Choosing between leasing a prestigious, trustworthy name to secure a vital bank loan or keeping your humble birth name.",
      "Deciding whether to lend your expensive name to an unregistered refugee friend so they can receive emergency medical care.",
      "Navigating the temptation to bid on a disgraced aristocrat's famous name at auction for ten cents on the dollar.",
      "Choosing whether to accept a corporate sponsorship that requires legally changing your first name to an industrial fertilizer brand.",
      "Balancing personal emotional attachment to a syllable against the practical financial needs of your family."
    ],
  },
  {
    id: "mirrors_as_windows",
    ruleTitle: "The Reflexive Architecture",
    premise: "Looking glasses do not reflect our room; they look directly into identical houses in an adjacent timeline where our counterpart is looking back.",
    socialOrder: "Every bathroom and parlor mirror is a silent, two-way window into a stranger's life; mirror covers are drawn for privacy or grief.",
    dailyPractice: "People wave good morning to their reflection-counterparts, exchanging silent pantomimes of daily recipes and weather signs.",
    civicVibe: "A contemplative, doubly-populated civilization where interior decor is arranged in conscious symmetry with the other side.",
    localParadigm: "Living in perpetual, gentle communion with an alternate self across a pane of unbroken optical glass.",
    forbiddenOrRevered: "Breaking a mirror is viewed as violently abandoning an innocent companion timeline.",
    consequences: [
      "Interior designers consult both households across the glass to coordinate matching wallpaper and furniture layouts.",
      "A person whose counterpart passes away covers their mirrors in black crepe and lives in unbearable unilateral isolation.",
      "Lovers write messages in mirror steam with grease pencils, carrying on silent, decades-long cross-dimensional romances.",
      "Children learn sign language before the spoken alphabet so they can gossip with their reflection-playmates across the hall.",
      "Burglaries are rare because reflection-counterparts immediately hammer on the glass to raise the alarm in both worlds.",
      "When one side falls ill, the other places hot herbal teas against the glass in a gesture of shared warmth and solidarity.",
      "Fashion trends evolve in tandem, with cities copying the subtle collar adjustments made by their counterparts across the silver.",
      "Philosophers spend lifetimes debating whether the other side has a soul or if both sides share a single divided spirit.",
      "Courts do not allow mirror testimonies because counterparts cannot be subpoenaed across the physical glass barrier.",
      "Mirrors in public taverns feature heavy brass shutters that are bolted shut during drunken brawls to protect innocent neighbors.",
      "A black market exists in antique mercury glass mirrors that reveal alternate timelines where historical wars went differently.",
      "Architects align streets so that opposing mirrors create infinite reflections that flicker with millions of parallel lives."
    ],
    dilemmas: [
      "Whether to break a sacred antique mirror to prevent your counterpart from watching you commit an act of desperate political sabotage.",
      "Choosing between following a career path you love or pursuing the path your counterpart across the glass is successfully charting.",
      "Deciding whether to warn your counterpart of an impending catastrophe by writing backwards on the steam, risking timeline contamination.",
      "Navigating the heartbreak of falling deeply in love with your counterpart's spouse, whom you watch every evening through the glass.",
      "Choosing whether to uncover a mirror that was veiled forty years ago following an unspeakable ancestral feud across the glass.",
      "Balancing the comforting companionship of your reflection against the profound desire to be truly, completely alone."
    ],
  },
];

/**
 * 12 unexpected role variants per InterestId reframed through the inversion lens.
 */
export const UNEXPECTED_INTEREST_ROLES: Record<InterestId, string[]> = {
  science: [
    "Alchemical Memory Distiller",
    "Nocturnal Nebula Metrologist",
    "Curator of Deliberate Anomalies",
    "Chronological Viscosity Sampler",
    "Acoustic Decibel Assayer",
    "Spun-Sugar Crystallographer",
    "Reflection Horizon Physicist",
    "Silhouette Density Surveyor",
    "Tactile Ellipsis Logician",
    "Ancillary Proxy Genealogist",
    "Inverted Aptitude Ergonomist",
    "Planetary Flaw Mathematician",
  ],
  art: [
    "Shadow Silhouette Tailor",
    "Edible Fresco Sculptor",
    "Typographical Error Illuminator",
    "Mirrored Pantomime Choreographer",
    "Nocturnal Phosphor Painter",
    "Pawned Memory Mosaicist",
    "Unfinished Clause Dramatist",
    "Subversive Competence Muralist",
    "Decibel-Dampened Felt Weaver",
    "Barley-Sugar Rose Carver",
    "Ancestor Portrait Emendator",
    "Syllabic Caligrapher of Blanks",
  ],
  music: [
    "Silent Ellipsis Composer",
    "Nocturnal Campanile Chimer",
    "Acoustic Decibel Smuggler",
    "Memory-Wafer Harmonium Tuner",
    "Sacrificial Wobble Acoustician",
    "Ancestral Proxy Hymnodist",
    "Reflexive Glass Echo Flutist",
    "Variable-Altitude Hydrolier",
    "Deliberate Discord Virtuoso",
    "Shadow-Rhythm Percussionist",
    "Clumsy Violin Troubadour",
    "Edible Chime Harmonizer",
  ],
  nature: [
    "Nocturnal Nightshade Cultivator",
    "Sound-Absorbing Moss Forager",
    "Compostable Sourdough Forester",
    "Temporal Eddy Beekeeper",
    "Bioluminescent Algae Apiarist",
    "Memory-Infused Herb Harvester",
    "Wild Shadow Wrangler",
    "Unpruned Wilderness Advocate",
    "Reflection Pond Arborist",
    "Ancestral Seed Guardian",
    "Clumsy Orchard Grafter",
    "Night-Blooming Orchid Shepherd",
  ],
  technology: [
    "Hydraulic Chronometer Smith",
    "Acoustic Baffle Engineer",
    "Memory-Wafer Stamping Machinist",
    "Pneumatic Ellipsis Router",
    "Deliberate Fault Quality Inspector",
    "Barley-Sugar Structural Mason",
    "Shadow-Filter Optical Grinder",
    "Rotational Persona Matrix Fitter",
    "Inverted-Talent Automaton Rigger",
    "Nocturnal Sodium Dynamo Keeper",
    "Ancestral Ballot Box Mechanician",
    "Two-Way Glass Silverer",
  ],
  people: [
    "Bureau of Apologies & Tea Director",
    "Ancestral Proxy Conciliator",
    "Silent Sentence Mediator",
    "Quadrennial Persona Allocator",
    "Acoustic Decibel Relief Worker",
    "Memory Redemption Advocate",
    "Incompetence Support Guild Lead",
    "Toddler Magistrate Interpreter",
    "Reflection Kinship Counselor",
    "Nocturnal Market Ombudsman",
    "Defect Acceptance Facilitator",
    "Name Donation Coordinator",
  ],
  stories: [
    "Unwritten Masterpiece Chronicler",
    "Pawned Memory Folklorist",
    "Unfinished Sentence Balladeer",
    "Departed Proxy Biographer",
    "Anonymous Quadrennial Fabulist",
    "Deliberate Typo Archivist",
    "Mirror-Steam Epigrammatist",
    "High-Altitude Time Scribe",
    "Nocturnal Mythos Reciter",
    "Shadow Silhouette Historian",
    "Inverted Talent Satirist",
    "Edible Parchment Novelist",
  ],
  adventure: [
    "High-Altitude Time-Vortex Scout",
    "Subterranean Silence Smuggler",
    "Nocturnal Shutter Breaker",
    "Unmapped Shadow Forager",
    "Forgotten Memory Dredger",
    "Forbidden Daylight Pioneer",
    "Eroded Biscuit Bridge Scaler",
    "Quadrennial Persona Stowaway",
    "Mirror Horizon Drifter",
    "Ancestral Boundary Crosser",
    "Clumsy Frontier Wayfarer",
    "Deliberate Labyrinth Explorer",
  ],
};

/**
 * 6 unexpected role suffixes shaped by LifePathId.
 */
export const UNEXPECTED_LIFEPATH_STYLES: Record<LifePathId, string[]> = {
  rooted: [
    "Sanctuary Keeper",
    "District Ward Trustee",
    "Hearth Custodian",
    "Old Quarter Steward",
    "Neighborhood Anchor",
    "Local Commons Caretaker",
  ],
  scholar: [
    "Axiomatic Dissector",
    "Academy Inquisitor",
    "Treatise Emendator",
    "Archive Decipherer",
    "Theoretical Cartographer",
    "Curator of Primary Records",
  ],
  maker: [
    "Bespoke Framework Artisan",
    "Tactile Prototyper",
    "Hand-Joinery Mechanician",
    "Manual Foundry Caster",
    "Workshop Fabricator",
    "Kinetic Assembler",
  ],
  voyager: [
    "Itinerant Courier",
    "Perimeter Flâneur",
    "Boundary Traverse Scout",
    "Wandering Caravan Liaison",
    "Waystation Wayfarer",
    "Horizon Drifter",
  ],
  guide: [
    "Quiet Mentor",
    "Crisis Wayfinder",
    "Pilgrim Accompanist",
    "Apprentice Counselor",
    "Consensus Weaver",
    "Sanctuary Conductor",
  ],
  founder: [
    "Guild Initiator",
    "Charter Instigator",
    "Subversive Syndicate Lead",
    "Independent Project Director",
    "Reform Lodge Convener",
    "Experimental Enterprise Founder",
  ],
};

/**
 * Combinatorial Tagline components: >= 8 starters x >= 8 pivots = 64+ combinations.
 */
export const TAGLINE_STARTERS = [
  "Trading predictable solemnity for",
  "Where your most overlooked quirk became",
  "Finding that abandoning conventional perfection opened",
  "A timeline where you turned an ordinary assumption upside down,",
  "Reframing sharp analytical rigor into",
  "Discovering that true leadership often resembles",
  "Where you surrendered the demand to be understood and",
  "A tender, mischievous existence rooted in",
  "Choosing an unexpected detour that revealed",
  "Where the laws of daily life inverted, and you",
];

export const TAGLINE_PIVOTS = [
  "a life of astonishing, deliberate grace.",
  "the foundation of an entire counter-cultural institution.",
  "doors to wonder no standard career could ever unlock.",
  "and everyone around you lived with far greater breathing room.",
  "orchestrating quiet, transformative daily surprises.",
  "a celebration where no one is required to prove their worth.",
  "found yourself suddenly, profoundly at home.",
  "the quiet art of taking yourself beautifully lightly.",
  "that staying flawed was the most generous gift you had to offer.",
  "learned to move through the world like light through honey.",
];

/**
 * Recombinable Story Origin parts: >= 12 variants per slot.
 */
export const ORIGIN_SETTINGS = [
  "Raised along the cobblestone terraces of {city}, where {daily_rule} was the first lesson taught to toddlers,",
  "Growing up within a modest timber townhouse overlooking the central clocktower of {city},",
  "Formed in the bustling market quarter where merchants bartered openly under the shadow of {city}'s grand arcade,",
  "In an unassuming hillside dwelling where windows were shuttered tightly against {city}'s damp evening mists,",
  "Surrounded by the rhythmic clang of repair workshops along the canal basin in {city},",
  "Born into a respected family of civic registrars within the austere municipal district of {city},",
  "Spending formative years in the crowded communal tenements nestled beside {city}'s grand library vaults,",
  "Raised in an eccentric household situated directly above an unmapped alley in the heart of {city},",
  "Growing up along the outer river embankment where the city's strict ordinances met rural lawlessness,",
  "Nurtured inside an old multi-generational courtyard where three families shared a single bread oven in {city},",
  "Trained from early childhood along the quiet, limestone colonnades of {city}'s philosophical district,",
  "In a drafty third-floor garret overlooking {city}'s central railway junctions and shunting yards,",
];

export const ORIGIN_FORMATIVE_EVENTS = [
  "{name} caused a minor civic scandal at age fifteen by turning an official inspection into an impromptu carnival.",
  "{name} discovered early on that following the letter of the law always produced an absurd, delightful contradiction.",
  "{name} was quietly reprimanded by local schoolmasters for questioning why adults took their solemn titles so seriously.",
  "{name} spent long afternoons secretly drawing satirical caricatures in the margins of official town ledgers.",
  "{name} became famous across the neighborhood for repairing broken clocks with mismatched brass buttons and beeswax.",
  "{name} refused to compete in the regional excellence trials, spending the examination day feeding stray cats on the river wall.",
  "{name} quietly replaced all the formal neighborhood warning signs with poetic, mischievous aphorisms.",
  "{name} realized that while peers memorized dry formulas for advancement, true insight lived in unexamined daily blunders.",
  "{name} staged a midnight puppet show from an attic window that brought three estranged neighborhood factions to tears.",
  "{name} spent entire seasons cataloging how sunlight refracted through discarded apothecary bottles rather than studying grammar.",
  "{name} discovered an unquenchable urge to puncture pomposity wherever authorities wore oversized velvet collars.",
  "{name} traded an expensive family heirloom dictionary for an antique street organ and a sack of roasted hazelnuts.",
];

export const ORIGIN_EARLY_INSIGHTS = [
  "This instilled an enduring conviction that dignity is found not in rigid posture, but in generous, unhurried presence.",
  "The experience permanently cured {name} of the anxious hunger for conventional establishment approval.",
  "From that moment forward, {name} measured success by how many closed doors could be opened with gentle humor.",
  "It established the idiosyncratic working method that would define every subsequent milestone of {name}'s unconventional career.",
  "Learning to laugh at pompous certainty became {name}'s primary armor against civilizational despair.",
  "This unexpected detour demonstrated that the most durable truths are often wrapped in deliberate playfulness.",
  "It cemented an instinctive distrust of standardized systems that treat human beings as interchangeable cogs.",
  "The resulting domestic reprimands taught {name} how to cultivate an impenetrable, cheerful independence.",
  "By sixteen, {name} had learned to turn every bureaucratic roadblock into an elegant springboard for creative mischief.",
  "This early defiance proved that one does not need to overthrow the world to build an authentic life inside it.",
  "It laid the groundwork for an enduring personal philosophy: when the game is rigged to be miserable, change the rules.",
  "From then on, {name} approached high matters of state with the serene, unbothered calm of a seasoned baker.",
];

/**
 * Recombinable Story Turning Point parts: Ambition x LifePath x Event Type.
 */
export const TURNING_POINT_PREMISES: Record<AmbitionId, string[]> = {
  understand: [
    "When presented with a prestigious laboratory fellowship to dissect fundamental reality with cold, dry instruments,",
    "Faced with an academic ultimatum to publish an orthodox thesis that explained away the world's most glorious anomalies,",
    "While sitting through an interminable symposium on the absolute certainty of cosmological mechanics,",
  ],
  build: [
    "Contracted by the municipal board to construct a monumental, brutalist administrative tower of polished concrete,",
    "When commercial developers offered an immense sum to mass-produce standardized modular housing units,",
    "Presented with blueprints for a terrifyingly efficient, automated factory designed to eliminate human touch entirely,",
  ],
  help: [
    "When a dreary winter depression and rising administrative indifference gripped the river ward's poorest quarters,",
    "Refusing to participate in high-stakes court litigation that would have evicted forty struggling tenant families,",
    "When bureaucratic regulations threatened to shutter an informal soup kitchen and shelter for wayward apprentices,",
  ],
  lead: [
    "Accidentally nominated to a vacant seat on the High Civic Council as an elaborate satirical joke by neighborhood tradesfolk,",
    "When rival ideological factions threatened to tear the district apart over conflicting interpretations of ancestral law,",
    "Appointed to direct the regional coordination committee during an unprecedented administrative breakdown,",
  ],
  create: [
    "Told by prestigious gallery critics that serious art must be grim, monochromatic, and thoroughly despairing,",
    "Commissioned to design an official civic monument celebrating an aggressive military annexation,",
    "When an executive publishing consortium offered lifelong financial security in exchange for sanitized, predictable pulp novels,",
  ],
  wander: [
    "At the exact moment when a secure, lifelong pensioned administrative clerkship was laid out on a silver tray,",
    "Confronted with the comfortable, stagnant routine of an inherited suburban estate surrounded by clipped boxwood hedges,",
    "When the state passport office offered an elite diplomatic courier post with strict, pre-approved travel corridors,",
  ],
};

export const TURNING_POINT_LIFEPATH_ACTIONS: Record<LifePathId, string[]> = {
  rooted: [
    "{name} walked out of the council chambers, went straight back to the old workshop hearth, and resolved to build a local refuge.",
    "{name} tore up the lucrative contract, dedicated personal savings to local soil, and anchored the project in neighborly trust.",
  ],
  scholar: [
    "{name} closed the official leather-bound registers, pulled out a stack of scrap parchment, and drafted a radical, irreverent counter-treatise.",
    "{name} challenged the senior faculty directly, proving with pinpoint mathematical wit that their rigid axioms were built on quicksand.",
  ],
  maker: [
    "{name} turned to the workbench, picked up hand chisels, and physically dismantled the standardized prototype into kinetic wonders.",
    "{name} stayed awake for three consecutive nights handcrafting bespoke wooden apparatuses that exposed the flaw in the official blueprints.",
  ],
  voyager: [
    "{name} packed a single weathered canvas knapsack, clipped custom wings to an antique bicycle, and pedaled past the outer checkpoints.",
    "{name} boarded a slow-moving grain barge down the unmapped canals, trading lively harmonica tunes and baked pies for passage.",
  ],
  guide: [
    "{name} invited both opposing delegations into a locked workshop filled with hot tea, fresh scones, and childhood board games.",
    "{name} stepped calmly between the shouting factions, offering gentle parables that disarmed their collective pomposity.",
  ],
  founder: [
    "{name} drafted a radical four-page manifesto on butcher paper and launched an independent, unincorporated cooperative guild before dawn.",
    "{name} pulled together twelve offbeat neighborhood artisans to form an alternative guild that operated entirely on barter and mutual aid.",
  ],
};

export const TURNING_POINT_OUTCOMES = [
  "This single decision permanently ruptured relations with high authorities, but earned the everlasting devotion of the common streets.",
  "The resulting scandal shook municipal polite society, yet established a vibrant cultural institution that survives to this day.",
  "It caused three months of terrifying financial panic, balanced by the breathtaking exhilaration of uncompromised personal autonomy.",
  "Though stripped of official credentials, {name} became a beloved folk hero whose practical advice was sought far above that of judges.",
  "The maneuver proved that an audacious refusal, delivered with absolute good humor, can dismantle decades of bureaucratic paralysis.",
  "It transformed what should have been a ruinous professional catastrophe into the happiest turning point of {name}'s entire life.",
  "Rather than bankrupting the endeavor, the audacity of the move inspired hundreds of quiet sympathizers to offer covert material aid.",
  "By dusk, the crisis had dissolved into laughter, proving that taking oneself lightly was the most lethal weapon against tyranny.",
  "The establishment never quite forgave the embarrassment, but they were forced to adopt {name}'s methods simply to keep up.",
  "It cemented {name}'s reputation as someone who could turn an impossible administrative deadlock into an afternoon festival.",
  "This bold pivot laid the foundation for an enduring community network that outlived three consecutive municipal administrations.",
  "From that day forward, {name} was completely ungovernable by ordinary fear, having already stared down their worst-case scenario.",
];

/**
 * Recombinable Story Today parts: >= 12 variants per slot.
 */
export const TODAY_SETTINGS = [
  "Today, {name} can be found behind the counter of a delightfully chaotic workshop in {city},",
  "Life has settled into a rhythmic, deeply satisfying routine in an old converted watermill on the edge of {city},",
  "Living in an airy, sun-drenched attic studio smelling of toasted cedar, roasted almonds, and ink,",
  "Working from an eccentric, open-air laboratory perched high on the limestone bluffs overlooking {city},",
  "Tucked away in a quiet neighborhood courtyard where wisteria vines climb over stacks of antique drafting linen,",
  "Operating an informal neighborhood dispensary of odd mechanical contraptions and gentle advice,",
  "Dwelling aboard a brightly painted canal barge moored just past the city's second stone bridge,",
  "Occupying an antique timber carriage house whose doors are kept unlatched for neighbors seeking urgent counsel,",
  "Perched on a rooftop terrace overlooking {city}'s slate gables and evening stove smoke,",
  "Running an unpretentious, subterranean printshop whose letterpress clatters softly throughout the morning,",
  "Surrounded by hanging copper instruments, drying herbal tea bundles, and stacks of well-thumbed notebooks,",
  "Holding court at a scarred oak table in the rear corner of the neighborhood's oldest tavern,",
];

export const TODAY_ACTIVITIES = [
  "spending mornings testing experimental kinetic toys and afternoons drinking spiced cider with passing apprentices.",
  "tuning delicate brass gears while listening to the troubles of neighbors who never quite understood official court forms.",
  "baking sourdough loaves that carry handwritten philosophical riddles baked directly into the crust.",
  "mentoring a ragtag collection of brilliant, unclassifiable youths who were expelled from standard academies.",
  "coordinating a subterranean mutual-aid network that quietly solves civic crises long before municipal inspectors notice.",
  "sculpting whimsical kinetic street clocks that ring unexpected tunes whenever politicians pass beneath.",
  "writing anonymous letters of affectionate encouragement to exhausted municipal clerks across the city.",
  "drafting uncommissioned architectural blueprints for public parks that feature giant slides and fruit orchards.",
  "repairing weathered maritime instruments for elderly sailors while exchanging memories of impossible harbors.",
  "calibrating delicate balances that weigh intangible things like neighborly kindness and evening peace.",
  "turning discarded industrial brass scrap into singing weather-vanes for local rooftop apiaries.",
  "flying hand-painted kites into the twilight breeze, each trailing tiny lanterns that glow against indigo clouds.",
];

export const TODAY_REFLECTIONS = [
  "There is very little money in the bank vault, but the pantry is overflowing with honey and bread from grateful companions.",
  "Dignified officials still shake their heads at {name}'s eccentric attire, but it is {name}'s creative mischief they seek in a crisis.",
  "With flour on the apron and grease on the cuffs, {name} moves with an effortless lightness that makes everyone feel instantly braver.",
  "Sitting back with a quiet smile as another kinetic contraption rings the dinner bell, {name} knows this life possesses authentic joy.",
  "Looking out across the rooftops as evening bells chime, {name} feels zero urge to trade this freedom for all the gold in the treasury.",
  "Life is delightfully messy and rarely predictable, exactly as an honest human existence ought to be.",
  "The hands are calloused and the hair is rumpled, but {name} sleeps through the night with a conscience clear as rainwater.",
  "Neighbors know {name} as the person who remembers everyone's birthday and never charges for an urgent repair.",
  "Watching the stars emerge over the city, {name} is deeply grateful for every detour, blunder, and broken rule that led home.",
  "There are no marble statues in the square, but {name}'s legacy is written into the courage and laughter of an entire district.",
  "Moving through the crowd with an easy, unhurried stride, {name} remains living proof that the expected path is rarely the best one.",
  "Sitting beside the hearth as the tea kettle whistles, {name} toasts the mysterious universe that made room for such wonderful foolishness.",
];

/**
 * 12 Choice set templates with choice/roadNotTaken/consequence from parts.
 */
export const CHOICE_TEMPLATES = [
  {
    age: "Age 19",
    choice: "Walked out of the final civil service exam to join an itinerant troupe of mechanical puppeteers.",
    roadNotTaken: "Completing a safe, lucrative career in chartered municipal revenue management.",
    consequence: "Empty pockets for three years, balanced by learning the core secrets of kinetic wonder.",
  },
  {
    age: "Age 26",
    choice: "Converted an abandoned municipal bathhouse into a free community workshop for unclassifiable inventions.",
    roadNotTaken: "Selling the prime real estate parcel to commercial high-rise warehouse developers.",
    consequence: "Created an enduring sanctuary where over two hundred offbeat local inventors found their voice.",
  },
  {
    age: "Age 34",
    choice: "Refused an honorary knighthood because the ceremony required wearing formal black velvet instead of bright yellow.",
    roadNotTaken: "Accepting formal establishment approval, a state pension, and civic peerage.",
    consequence: "Became a beloved folk legend among ordinary tradesfolk while permanently baffling court aristocrats.",
  },
  {
    age: "Age 18",
    choice: "Traded an expensive family heirloom pocket watch for a battered brass cornet and an antique bicycle.",
    roadNotTaken: "Keeping the watch tucked securely inside an executive clerk's waistcoat.",
    consequence: "Spent two joyous summers busking across coastal fishing hamlets learning regional folklore.",
  },
  {
    age: "Age 27",
    choice: "Staged the Great Lantern Flight during the city's dreariest, rainiest November evening on record.",
    roadNotTaken: "Staying inside quietly by the stove like every other respectable citizen.",
    consequence: "Single-handedly lifted the morale of twenty thousand damp, demoralized residents.",
  },
  {
    age: "Age 35",
    choice: "Wrote a bestselling treatise on municipal tax law where every chapter was accompanied by a humorous fairy tale.",
    roadNotTaken: "Publishing a dry, standard academic compilation of administrative ratios.",
    consequence: "Inspired accounting clerks across three provinces to view their ledgers with creative humanity.",
  },
  {
    age: "Age 21",
    choice: "Resigned from the state geological survey to build pedal-powered flying gliders from bamboo and silk.",
    roadNotTaken: "A lifetime of stable civil service pension, heavy field boots, and routine quarry surveys.",
    consequence: "Crashed into six haystacks, but ultimately designed the province's safest low-altitude glider.",
  },
  {
    age: "Age 29",
    choice: "Secretly added chamomile and cinnamon to the municipal water reservoir's trial herbal filter tanks.",
    roadNotTaken: "Sticking strictly to plain, tasteless mineral chlorination protocols.",
    consequence: "Caused an unprecedented citywide outbreak of polite manners, laughter, and peaceful afternoon naps.",
  },
  {
    age: "Age 37",
    choice: "Adopted six retired circus mules to create a free, traveling lending library across mountain villages.",
    roadNotTaken: "Retiring into a comfortable, gated seaside townhouse with an ornamental hedge.",
    consequence: "Brought literacy, fresh apples, and warm woolen blankets to hundreds of isolated mountain families.",
  },
  {
    age: "Age 20",
    choice: "Turned down a scholarship to the Royal Academy of Dramatic Arts to bake sourdough for street performers.",
    roadNotTaken: "Pursuing prestigious classical theatrical notoriety in the national capital.",
    consequence: "Mastered culinary chemistry while maintaining an fiercely independent, joyful creative spirit.",
  },
  {
    age: "Age 28",
    choice: "Organized the Midnight Roller-Skate Regatta through the marble corridors of city hall during budget debates.",
    roadNotTaken: "Attending the sober, dreary annual municipal finance banquet in stiff evening wear.",
    consequence: "Briefly detained by palace guards, but permanently cured the city elders of their self-importance.",
  },
  {
    age: "Age 36",
    choice: "Declined a corporate buyout of their artisan toy workshop, leaving the company to a trust for local youths.",
    roadNotTaken: "Retiring early as an independent multi-millionaire on a private Mediterranean yacht.",
    consequence: "Preserved a living center of joyful craftsmanship and mutual aid for generations to come.",
  },
];
